import { useQuery } from "@tanstack/react-query";
import {
  CategoryStandardProduct,
  StandardProductCategory,
  StandardProductCategoryProduct,
} from "shared/api/middleware-api-client";
import {
  STANDARD_PRODUCT_CATEGORIES_QUERY_KEY,
  fetchStandardProductCategoriesData,
} from "./standardProductCategoriesQuery";
import {
  CATALOG_DATA_CACHE_TIME,
  CATALOG_DATA_STALE_TIME,
} from "shared/config/queryConstants";

// ─── Shared base query options ────────────────────────────────────────────────

const baseQueryOptions = {
  queryKey: [STANDARD_PRODUCT_CATEGORIES_QUERY_KEY] as const,
  queryFn: fetchStandardProductCategoriesData,
  staleTime: CATALOG_DATA_STALE_TIME,
  cacheTime: CATALOG_DATA_CACHE_TIME,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

// ─── Selector functions ─────────────────────────────────

export const selectProductsByID = (
  categories: StandardProductCategory[],
): Map<string, CategoryStandardProduct> => {
  const montageCategory =
    categories.find((c) => c.code === "montageservice") ?? null;

  const map = new Map<string, CategoryStandardProduct>();
  const list = (montageCategory?.product ??
    []) as StandardProductCategoryProduct[];
  for (const p of list) map.set(p.standardProductId, p.standardProduct);
  return map;
};

export const selectRecommendedPanels = (
  categories: StandardProductCategory[],
): CategoryStandardProduct[] =>
  (categories[0]?.product ?? [])
    .map((item: StandardProductCategoryProduct) => item.standardProduct)
    .slice(0, 2);

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useStandardProductCategories = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { isLoading, isError, data, error } = useQuery({
    ...baseQueryOptions,
    enabled,
  });

  return {
    categoriesLoading: isLoading,
    isError,
    standardProductCategories: data,
    error,
  };
};

export const useStandardProductCategoriesByID = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { data: productsById } = useQuery({
    ...baseQueryOptions,
    enabled,
    select: selectProductsByID,
  });

  return { productsById };
};

export const useRecommendedPanels = () => {
  const { isLoading, data: recommendedPanels } = useQuery({
    ...baseQueryOptions,
    select: selectRecommendedPanels,
  });

  return {
    panelsLoading: isLoading,
    recommendedPanels,
  };
};
